import type {
  ClosePermissionReply,
  ClosePermissionRequest,
  ClosePermissionStatus,
} from '../shared/close-permission';
import { primaryReviewChannel } from './review-channel';
import { holdForReviewEdits } from '../paige/leave-warning';

type Blocker = { read: () => ClosePermissionStatus; retired: boolean };
/** One controller per renderer. No document contents cross this protocol. */
export class RendererClosePermission {
  private active: ClosePermissionRequest | undefined;
  private readonly barriers = new Set<number>();
  private readonly blockers = new Set<Blocker>();
  private barrierSender: ((requestId: string, participantId: number) => void) | undefined;
  private notice: Exclude<ClosePermissionStatus, 'idle'> | undefined;
  private readonly listeners = new Set<() => void>();
  constructor(
    private readonly primary: boolean,
    private readonly reply: (reply: ClosePermissionReply) => void,
  ) {}
  get frozen() {
    return Boolean(this.active);
  }
  getNotice = () => this.notice;
  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };
  dismiss = () => {
    this.notice = undefined;
    for (const listener of this.listeners) listener();
  };
  register(read: () => ClosePermissionStatus) {
    const blocker = { read, retired: false };
    this.blockers.add(blocker);
    return () => {
      // An admitted writer can outlive its React view. Keep that tail until it
      // settles, without treating a view's disappearance as a new write.
      if (read() === 'saving') blocker.retired = true;
      else this.blockers.delete(blocker);
    };
  }
  setBarrierSender(sender: ((requestId: string, participantId: number) => void) | undefined) {
    this.barrierSender = sender;
  }
  receive(request: ClosePermissionRequest) {
    if (request.phase === 'cancel') {
      if (request.requestId === this.active?.requestId) {
        this.active = undefined;
        this.barriers.clear();
        this.notice = request.reason;
        for (const listener of this.listeners) listener();
      }
      return;
    }
    if (request.phase === 'freeze') {
      if (
        this.active &&
        (this.active.requestId !== request.requestId || this.active.phase !== 'freeze')
      )
        return;
      this.active = request;
      this.respond();
      return;
    }
    if (
      !this.active ||
      request.requestId !== this.active.requestId ||
      request.participantId !== this.active.participantId
    )
      return;
    if (request.phase === 'check' && this.active.phase !== 'freeze') return;
    if (request.phase === 'authorize' && this.active.phase !== 'check') return;
    this.active = request;
    if (request.phase === 'check' && !this.primary) {
      if (!this.barrierSender) {
        this.respond('unavailable');
        return;
      }
      // This is the SAME channel instance used by this panel's edit/confirm
      // actions. The owner cannot approve before their preceding messages.
      this.barrierSender(request.requestId, request.participantId);
    }
    this.respond();
  }
  noteBarrier(requestId: string, participantId: number) {
    if (
      !this.primary ||
      requestId !== this.active?.requestId ||
      !this.active.participantIds.includes(participantId)
    )
      return;
    this.barriers.add(participantId);
    if (this.active.phase === 'check') this.respond();
  }
  private status(): ClosePermissionStatus {
    let status: ClosePermissionStatus = 'idle';
    for (const blocker of this.blockers) {
      let value: ClosePermissionStatus;
      try {
        value = blocker.read();
      } catch {
        return 'unavailable';
      }
      if (blocker.retired && value !== 'saving') {
        this.blockers.delete(blocker);
        continue;
      }
      if (value === 'saving') return 'saving';
      if (value === 'drafts' || value === 'unavailable') status = value;
    }
    return status;
  }
  private respond(forced?: ClosePermissionStatus) {
    const request = this.active;
    if (!request || request.phase === 'cancel') return;
    if (
      !forced &&
      this.primary &&
      request.phase === 'check' &&
      request.participantIds.some((id) => id !== request.participantId && !this.barriers.has(id))
    )
      return;
    this.reply({
      requestId: request.requestId,
      phase: request.phase,
      status: forced ?? this.status(),
    });
  }
}

let controller: RendererClosePermission | undefined;
export const closePermissionFrozen = () => controller?.frozen ?? false;
// PAIGE (docs/engineering/web.md, "When Paige is updated"): the browser has no close controller;
// what would veto the desktop's close holds the page against Paige reloading itself instead, and
// makes the browser ask before the tab is closed or reloaded (paige/leave-warning.ts).
export const registerCloseBlocker = (read: () => ClosePermissionStatus) =>
  controller?.register(read) ?? holdForReviewEdits(read);
export const setCloseBarrierSender = (
  sender: ((requestId: string, participantId: number) => void) | undefined,
) => controller?.setBarrierSender(sender);
export const noteCloseBarrier = (requestId: string, participantId: number) =>
  controller?.noteBarrier(requestId, participantId);
export const closePermissionNotice = () => controller?.getNotice();
export const subscribeClosePermissionNotice = (listener: () => void) =>
  controller?.subscribe(listener) ?? (() => {});
export const dismissClosePermissionNotice = () => controller?.dismiss();
export const installClosePermission = (primary: boolean) => {
  const desktop = window.capturePointDesktop;
  if (!desktop?.onClosePermissionRequest || !desktop.replyClosePermission || controller) return;
  controller = new RendererClosePermission(primary, (reply) => {
    void desktop.replyClosePermission(reply).catch(() => {});
  });
  if (primary) primaryReviewChannel(noteCloseBarrier);
  // Do not disable/inert/focus editors here: that can synthesize a saving blur.
  // The capture guard and store admission predicate take effect synchronously.
  const freezeEvent = (event: Event) => {
    if (!closePermissionFrozen()) return;
    event.preventDefault();
    event.stopImmediatePropagation();
  };
  for (const name of [
    'beforeinput',
    'input',
    'change',
    'keydown',
    'keyup',
    'pointerdown',
    'pointerup',
    'mousedown',
    'mouseup',
    'click',
    'dblclick',
    'submit',
    'blur',
    'focusout',
    'paste',
    'cut',
    'drop',
    'compositionstart',
    'compositionupdate',
    'compositionend',
  ])
    window.addEventListener(name, freezeEvent, true);
  desktop.onClosePermissionRequest((request) => controller!.receive(request));
};
